import { useCallback, useMemo } from "react"

import { useAuthentication } from "@/contexts"
import type { Organization } from "@/models/organization"
import { useLocalStorage } from "@uidotdev/usehooks"

export interface UserState {
  organizationId: string
}

export type UsersState = Record<string, UserState>

interface UseUserStateValue {
  saveActiveOrganization: (organizationId: Organization["id"]) => void
  userState: UserState
}

export const emptyUserState: UserState = {
  organizationId: "",
}

export const useUserState = (): UseUserStateValue => {
  const { authenticatedUser } = useAuthentication()

  const [usersState, setUsersState] = useLocalStorage<UsersState>(
    `${process.env.REACT_APP_ENV}UserSessionInfo`,
    {}
  )

  const userId = authenticatedUser?.id

  const currentUserState = useMemo(
    () => (userId ? (usersState[userId] ?? emptyUserState) : emptyUserState),
    [userId, usersState]
  )

  const updateUserState = useCallback(
    (updates: Partial<UserState>): void => {
      if (!userId) {
        return
      }

      setUsersState({
        ...usersState,
        [userId]: { ...currentUserState, ...updates },
      })
    },
    [userId, setUsersState, usersState, currentUserState]
  )

  const saveActiveOrganization = useCallback(
    (organizationId: Organization["id"]): void => {
      updateUserState({ organizationId })
    },
    [updateUserState]
  )

  return useMemo(() => {
    return {
      saveActiveOrganization,
      userState: currentUserState,
    }
  }, [saveActiveOrganization, currentUserState])
}
